import React from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { warningTriangleIcon } from "@progress/kendo-svg-icons";
import { RawFileUploadConflict } from "@application/useCases/rawFiles/UploadRawFileService";

interface UploadConflictDialogProperties {
  conflicts: RawFileUploadConflict[];
  /**
    Number of selected files that do not conflict and would upload normally.
    */
  nonConflictingCount: number;
  folder: string;
  onOverwriteAll: () => void;
  onSkipConflicting: () => void;
  onCancel: () => void;
}

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;

  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex++;
  }

  return `${value.toFixed(1)} ${units[unitIndex]}`;
};

export const UploadConflictDialog: React.FC<UploadConflictDialogProperties> = ({ conflicts, nonConflictingCount, folder, onOverwriteAll, onSkipConflicting, onCancel }) => {
  const conflictCount = conflicts.length;
  const plural = conflictCount === 1 ? "file" : "files";

  return (
    <Dialog title="Files already exist" onClose={onCancel} width={620}>
      <div style={{ padding: "10px" }}>
        <div role="alert" style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "12px", color: "#b26a00" }}>
          <SvgIcon icon={warningTriangleIcon} />
          <span>
            {conflictCount} {plural} you selected already exist in <strong>{folder || "Root"}</strong> with a different size. Continuing will permanently replace the stored {plural}.
          </span>
        </div>

        <div style={{ maxHeight: "220px", overflowY: "auto", border: "1px solid #eee", borderRadius: "4px", padding: "8px" }}>
          <ul aria-label="Conflicting files" style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {conflicts.map(({ file, existingSize }) => (
              <li key={file.name} style={{ padding: "4px 0", borderBottom: "1px solid #f5f5f5", fontSize: "0.85rem" }}>
                <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={file.name}>
                  {file.name}
                </div>
                <div style={{ fontSize: "0.75rem", color: "#666" }}>
                  Stored: {formatSize(existingSize)} → New: {formatSize(file.size)}
                </div>
              </li>
            ))}
          </ul>
        </div>

        {nonConflictingCount > 0 && (
          <p style={{ fontSize: "0.8rem", color: "#666", marginTop: "10px" }}>
            {nonConflictingCount} other selected {nonConflictingCount === 1 ? "file does" : "files do"} not conflict.
          </p>
        )}
      </div>

      <DialogActionsBar>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={onSkipConflicting} disabled={nonConflictingCount === 0}>
          Skip conflicting
        </Button>
        <Button themeColor="warning" onClick={onOverwriteAll}>
          Overwrite all
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};
